import { SkeletonPaymentsPage } from '@/components/ui/Skeleton';
import { PaymentDetailPanel, PaymentListItem, RejectReasonModal } from '@/features/treasury';
import { useToast } from '@/hooks/useToast';
import { api } from '@/lib/api';
import { formatTimeUntilAutoCancel, formatTimeUntilBusinessDeadline } from '@/lib/business-hours';
import type { PurchaseWithTickets } from '@eventflow/shared-types';
import { AlertTriangle, Flame, Clock, Paperclip, CheckCircle2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// =============================================================================
// TYPES
// =============================================================================

type FilterType =
  | 'all'
  | 'pending'
  | 'pending_with_proof'
  | 'pending_without_proof'
  | 'confirmed'
  | 'failed';

type DeadlineInfo = {
  label: string;
  rule: string;
  isExpired: boolean;
  isExpiringSoon: boolean;
  badgeClass: string;
  icon: React.ReactNode;
  hoursRemaining: number;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function TreasuryHubPage() {
  const { success: showSuccess, error: showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Referencias para navegación por teclado
  const listContainerRef = useRef<HTMLDivElement>(null);

  // Estado principal
  const [purchases, setPurchases] = useState<PurchaseWithTickets[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Filtros y ordenamiento
  const [filter, setFilter] = useState<FilterType>('pending_with_proof');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'oldest' | 'amount_high' | 'amount_low'>(
    'recent'
  );

  // Modal de rechazo
  const [showRejectModal, setShowRejectModal] = useState(false);

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  // Obtener información de deadline para un purchase
  const getDeadlineInfo = useCallback((purchase: PurchaseWithTickets): DeadlineInfo | null => {
    if (purchase.payment_status !== 'pending') return null;

    const seatCount = purchase.tickets.length;
    const deadlineHours = purchase.event?.payment_confirmation_deadline_hours;

    if (deadlineHours === null) return null;

    const timeInfo =
      deadlineHours !== undefined && deadlineHours > 0
        ? formatTimeUntilBusinessDeadline(purchase.created_at, deadlineHours)
        : formatTimeUntilAutoCancel(purchase.created_at, seatCount);

    const hasCustomDeadline = Boolean(deadlineHours !== undefined && deadlineHours > 0);
    const seatDescription = `${seatCount} asiento${seatCount !== 1 ? 's' : ''}`;
    const limitDescription = hasCustomDeadline
      ? ''
      : `Auto-cancelación: ${timeInfo.limitDays} día${timeInfo.limitDays !== 1 ? 's' : ''} hábil${timeInfo.limitDays !== 1 ? 'es' : ''} • ${seatDescription}`;

    let badgeClass = 'bg-blue-50 text-blue-700 border-blue-200';
    let icon: React.ReactNode = <Clock className="w-3 h-3" />;

    if (timeInfo.isExpired) {
      badgeClass = 'bg-red-50 text-red-700 border-red-200';
      icon = <AlertTriangle className="w-3 h-3" />;
    } else if (timeInfo.hours <= 24) {
      badgeClass = 'bg-red-100 text-red-700 border-red-200';
      icon = <Flame className="w-3 h-3" />;
    } else if (timeInfo.hours <= 48) {
      badgeClass = 'bg-yellow-50 text-yellow-700 border-yellow-200';
      icon = <Clock className="w-3 h-3" />;
    }

    return {
      label: timeInfo.isExpired ? 'Tiempo agotado' : timeInfo.formatted,
      rule: limitDescription,
      isExpired: timeInfo.isExpired,
      isExpiringSoon: !timeInfo.isExpired && timeInfo.hours <= 24,
      badgeClass,
      icon,
      hoursRemaining: timeInfo.hours,
    };
  }, []);

  // Filtrar y ordenar purchases
  const filteredPurchases = useMemo(() => {
    let result = [...purchases];

    // Filtro por estado
    if (filter !== 'all') {
      if (filter === 'pending_with_proof') {
        result = result.filter(
          (p) => p.payment_status === 'pending' && Boolean(p.payment_proof_url)
        );
      } else if (filter === 'pending_without_proof') {
        result = result.filter(
          (p) => p.payment_status === 'pending' && !Boolean(p.payment_proof_url)
        );
      } else if (filter === 'pending') {
        result = result.filter((p) => p.payment_status === 'pending');
      } else {
        result = result.filter((p) => p.payment_status === filter);
      }
    }

    // Búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((p) => {
        const customerName = p.customer_name?.toLowerCase() || '';
        const customerEmail = p.customer_email?.toLowerCase() || '';
        const eventName = p.event.name?.toLowerCase() || '';
        return (
          customerName.includes(query) || customerEmail.includes(query) || eventName.includes(query)
        );
      });
    }

    // Ordenar según sortBy
    result.sort((a, b) => {
      // Para pendientes, siempre priorizar con comprobante primero
      if (filter.startsWith('pending')) {
        const aHasProof = Boolean(a.payment_proof_url);
        const bHasProof = Boolean(b.payment_proof_url);
        if (aHasProof !== bHasProof) return aHasProof ? -1 : 1;
      }

      // Luego aplicar ordenamiento seleccionado
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'amount_high':
          return b.total_amount - a.total_amount;
        case 'amount_low':
          return a.total_amount - b.total_amount;
        case 'recent':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    return result;
  }, [purchases, filter, searchQuery, sortBy]);

  // Purchase seleccionado
  const selectedPurchase = useMemo(() => {
    if (!selectedPurchaseId) return null;
    return purchases.find((p) => p.id === selectedPurchaseId) || null;
  }, [purchases, selectedPurchaseId]);

  // Contadores para badges
  const counts = useMemo(
    () => ({
      all: purchases.length,
      pending: purchases.filter((p) => p.payment_status === 'pending').length,
      pendingWithProof: purchases.filter(
        (p) => p.payment_status === 'pending' && Boolean(p.payment_proof_url)
      ).length,
      pendingWithoutProof: purchases.filter(
        (p) => p.payment_status === 'pending' && !Boolean(p.payment_proof_url)
      ).length,
      confirmed: purchases.filter((p) => p.payment_status === 'confirmed').length,
      failed: purchases.filter((p) => p.payment_status === 'failed').length,
    }),
    [purchases]
  );

  // =============================================================================
  // API CALLS
  // =============================================================================

  const fetchPurchases = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<PurchaseWithTickets[]>('/purchases/organizer');
      setPurchases(data);
    } catch (err) {
      console.error('Error fetching purchases:', err);
      showError('Error al cargar las compras');
    } finally {
      setIsLoading(false);
    }
  };

  // =============================================================================
  // ACTIONS
  // =============================================================================

  const handleApprove = async () => {
    if (!selectedPurchase || isProcessing) return;

    const purchaseId = selectedPurchase.id;
    setIsProcessing(true);
    try {
      await api.post(`/purchases/${purchaseId}/confirm-payment`, {
        paymentStatus: 'confirmed',
      });

      // Actualizar estado local
      setPurchases((prev) =>
        prev.map((p) => (p.id === purchaseId ? { ...p, payment_status: 'confirmed' as const } : p))
      );

      showSuccess('¡Pago aprobado! El cliente recibirá sus tickets por email.');

      // Seleccionar siguiente
      const currentIndex = filteredPurchases.findIndex((p) => p.id === purchaseId);
      if (currentIndex < filteredPurchases.length - 1) {
        setSelectedPurchaseId(filteredPurchases[currentIndex + 1].id);
      } else if (currentIndex > 0) {
        setSelectedPurchaseId(filteredPurchases[currentIndex - 1].id);
      } else {
        setSelectedPurchaseId(null);
      }
    } catch (err) {
      console.error('Error approving payment:', err);
      showError(
        'Error al aprobar el pago: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!selectedPurchase || isProcessing) return;

    const purchaseId = selectedPurchase.id;
    setIsProcessing(true);
    try {
      await api.post(`/purchases/${purchaseId}/confirm-payment`, {
        paymentStatus: 'failed',
        notes: reason,
      });

      // Actualizar estado local
      setPurchases((prev) =>
        prev.map((p) =>
          p.id === purchaseId ? { ...p, payment_status: 'failed' as const, notes: reason } : p
        )
      );

      setShowRejectModal(false);
      showSuccess('Pago rechazado. Los asientos han sido liberados.');

      // Seleccionar siguiente
      const currentIndex = filteredPurchases.findIndex((p) => p.id === purchaseId);
      if (currentIndex < filteredPurchases.length - 1) {
        setSelectedPurchaseId(filteredPurchases[currentIndex + 1].id);
      } else if (currentIndex > 0) {
        setSelectedPurchaseId(filteredPurchases[currentIndex - 1].id);
      } else {
        setSelectedPurchaseId(null);
      }
    } catch (err) {
      console.error('Error rejecting payment:', err);
      showError(
        'Error al rechazar el pago: ' + (err instanceof Error ? err.message : 'Error desconocido')
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // =============================================================================
  // KEYBOARD NAVIGATION
  // =============================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Solo si no hay un input enfocado
      if (
        document.activeElement?.tagName === 'INPUT' ||
        document.activeElement?.tagName === 'TEXTAREA'
      ) {
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();

        if (filteredPurchases.length === 0) return;

        const currentIndex = selectedPurchaseId
          ? filteredPurchases.findIndex((p) => p.id === selectedPurchaseId)
          : -1;

        let newIndex: number;
        if (e.key === 'ArrowDown') {
          newIndex = currentIndex < filteredPurchases.length - 1 ? currentIndex + 1 : 0;
        } else {
          newIndex = currentIndex > 0 ? currentIndex - 1 : filteredPurchases.length - 1;
        }

        setSelectedPurchaseId(filteredPurchases[newIndex].id);

        // Scroll al item
        const listContainer = listContainerRef.current;
        if (listContainer) {
          const items = listContainer.querySelectorAll('button');
          items[newIndex]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }

      // Enter para aprobar (si tiene comprobante)
      if (e.key === 'Enter' && selectedPurchase?.payment_status === 'pending') {
        if (selectedPurchase.payment_proof_url) {
          handleApprove();
        }
      }

      // Escape para deseleccionar
      if (e.key === 'Escape') {
        setSelectedPurchaseId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPurchases, selectedPurchaseId]);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Cargar datos iniciales
  useEffect(() => {
    fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manejar parámetros de URL (desde búsqueda global)
  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    const highlightId = searchParams.get('highlight');

    if (searchFromUrl) {
      setSearchQuery(searchFromUrl);
      setFilter('all');
      setSearchParams({}, { replace: true });
    }

    if (highlightId) {
      setSelectedPurchaseId(highlightId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // =============================================================================
  // RENDER
  // =============================================================================

  if (isLoading) {
    return <SkeletonPaymentsPage />;
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Centro de Resolución de Pagos</h1>
            <p className="text-sm text-gray-500 mt-1">
              Revisa y concilia los comprobantes de pago de tus eventos
            </p>
          </div>

          {/* Stats rápidos */}
          <div className="flex items-center gap-3">
            {counts.pendingWithProof > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-emerald-700">
                  {counts.pendingWithProof} listo{counts.pendingWithProof !== 1 ? 's' : ''} para
                  revisar
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Filtros - Nivel 1 + Buscador */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
            <button
              onClick={() => setFilter('pending_with_proof')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                filter === 'pending' ||
                filter === 'pending_with_proof' ||
                filter === 'pending_without_proof'
                  ? 'bg-white text-yellow-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Pendientes ({counts.pending})
              </div>
            </button>
            <button
              onClick={() => setFilter('confirmed')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                filter === 'confirmed'
                  ? 'bg-white text-green-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Confirmados ({counts.confirmed})
              </div>
            </button>
            <button
              onClick={() => setFilter('failed')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                filter === 'failed'
                  ? 'bg-white text-red-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Rechazados ({counts.failed})
            </button>
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                filter === 'all'
                  ? 'bg-white text-gray-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Todos ({counts.all})
            </button>
          </div>

          {/* Buscador */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <input
              type="text"
              placeholder="Buscar cliente o evento..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Filtros - Nivel 2 (solo visible cuando está en Pendientes) */}
        {(filter === 'pending' ||
          filter === 'pending_with_proof' ||
          filter === 'pending_without_proof') && (
          <div className="flex gap-1 p-1 bg-yellow-50 border border-yellow-200 rounded-lg w-fit">
            <button
              onClick={() => setFilter('pending_with_proof')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                filter === 'pending_with_proof'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1">
                <Paperclip className="w-4 h-4" />
                Con comprobante ({counts.pendingWithProof})
              </div>
            </button>
            <button
              onClick={() => setFilter('pending_without_proof')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                filter === 'pending_without_proof'
                  ? 'bg-white text-orange-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Sin comprobante ({counts.pendingWithoutProof})
              </div>
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                filter === 'pending'
                  ? 'bg-white text-yellow-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Todos pendientes
            </button>
          </div>
        )}
      </div>

      {/* Master-Detail Layout */}
      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Lista izquierda (30%) */}
        <div className="w-[35%] flex flex-col bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Lista header */}
          <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {filteredPurchases.length} resultado{filteredPurchases.length !== 1 ? 's' : ''}
              </span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="text-xs px-2 py-1 bg-white border border-gray-200 rounded-md text-gray-600 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="recent">Más recientes</option>
                <option value="oldest">Más antiguos</option>
                <option value="amount_high">Mayor monto</option>
                <option value="amount_low">Menor monto</option>
              </select>
            </div>
          </div>

          {/* Lista de pagos */}
          <div ref={listContainerRef} className="flex-1 overflow-y-auto">
            {filteredPurchases.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                <svg
                  className="w-12 h-12 mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-sm">No hay pagos en este filtro</p>
              </div>
            ) : (
              filteredPurchases.map((purchase) => (
                <PaymentListItem
                  key={purchase.id}
                  purchase={purchase}
                  isSelected={selectedPurchaseId === purchase.id}
                  onClick={() => setSelectedPurchaseId(purchase.id)}
                  deadlineInfo={getDeadlineInfo(purchase)}
                />
              ))
            )}
          </div>
        </div>

        {/* Panel derecho (70%) */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <PaymentDetailPanel
            purchase={selectedPurchase}
            onApprove={handleApprove}
            onReject={() => setShowRejectModal(true)}
            isProcessing={isProcessing}
            deadlineInfo={selectedPurchase ? getDeadlineInfo(selectedPurchase) : null}
          />
        </div>
      </div>

      {/* Modal de rechazo */}
      <RejectReasonModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleReject}
        isLoading={isProcessing}
        customerName={selectedPurchase?.customer_name || ''}
      />
    </div>
  );
}
